import { useNavigate } from 'react-router-dom';
import { Box, Typography, Container, LinearProgress, Skeleton, Badge } from '@mui/material';
import { Coffee, CardGiftcard, ArrowForward } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import loyaltyService from '../../services/loyaltyService';
import { colors, designTokens, motion } from '../../theme/theme';
import { GlassCard, GradientCTA, EmptyState } from '../../components/shared';
import { useTranslation } from 'react-i18next';
import { queryKeys } from '../../utils/queryKeys';

export default function MyLoyaltyPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: cards = [], isLoading: loading } = useQuery({
    queryKey: queryKeys.loyalty.myCards(),
    queryFn: () => loyaltyService.getMyCards(),
  });

  if (loading) return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#FFFFFF' }}>{t('loyalty.my_cards')}</Typography>
      {[1, 2].map(i => <Skeleton key={i} variant="rounded" height={140} sx={{ mb: 2, borderRadius: `${designTokens.radius.lg}px`, bgcolor: 'rgba(255,255,255,0.04)' }} />)}
    </Container>
  );

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#FFFFFF' }}>
        {t('loyalty.my_cards')}
      </Typography>
      <Typography variant="body2" sx={{ color: colors.neutral[500], mb: 3 }}>
        Collect stamps at partner cafes. Earn free drinks!
      </Typography>

      {cards.length === 0 ? (
        <EmptyState
          icon={<Coffee />}
          title={t('loyalty.no_cards')}
          description={t('loyalty.visit_cafe')}
          cta={
            <GradientCTA size="md" onClick={() => navigate('/venues')}>
              {t('loyalty.find_cafes')}
            </GradientCTA>
          }
        />
      ) : (
        cards.map(card => (
          <GlassCard
            key={card.card_id}
            hoverable
            padding={2.5}
            onClick={() => navigate(`/loyalty/${card.card_id}`)}
            sx={{ mb: 2, transition: `transform ${motion.duration.base} ${motion.easing.standard}, border-color ${motion.duration.base} ${motion.easing.standard}` }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              {card.venue_photo ? (
                <Box component="img" src={card.venue_photo} alt={card.venue_name}
                  loading="lazy"
                  sx={{ width: 56, height: 56, borderRadius: 2, objectFit: 'cover', border: '1px solid rgba(255,255,255,0.06)' }} />
              ) : (
                <Box sx={{
                  width: 56, height: 56, borderRadius: 2,
                  bgcolor: colors.primary.transparent,
                  border: `1px solid ${colors.primary.main}33`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Coffee sx={{ color: colors.primary.main }} />
                </Box>
              )}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#FFFFFF' }}>{card.venue_name}</Typography>
                <Typography variant="body2" sx={{ color: colors.neutral[500] }}>
                  {card.total_stamps} total stamps · {card.rewards_earned} rewards earned
                </Typography>
              </Box>
              {card.rewards_available > 0 && (
                <Badge badgeContent={card.rewards_available} color="warning">
                  <CardGiftcard sx={{ color: colors.accent.main }} />
                </Badge>
              )}
              <ArrowForward sx={{ color: colors.neutral[400] }} />
            </Box>

            {/* Progress */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                {card.stamps_count} / {card.stamps_required} stamps
              </Typography>
              <Typography variant="caption" sx={{ color: colors.neutral[500] }}>
                {card.stamps_required - card.stamps_count > 0
                  ? `${card.stamps_required - card.stamps_count} more for free reward`
                  : 'Reward available!'}
              </Typography>
            </Box>
            <LinearProgress variant="determinate"
              value={(card.stamps_count / card.stamps_required) * 100}
              sx={{ height: 8, borderRadius: 4, bgcolor: 'rgba(255,255,255,0.06)',
                '& .MuiLinearProgress-bar': { bgcolor: colors.primary.main, borderRadius: 4 } }} />

            {/* Stamp dots */}
            <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, flexWrap: 'wrap' }}>
              {Array.from({ length: card.stamps_required }).map((_, i) => {
                const filled = i < card.stamps_count;
                return (
                  <Box key={i} sx={{
                    width: 22, height: 22, borderRadius: '50%',
                    bgcolor: filled ? colors.primary.main : 'rgba(255,255,255,0.04)',
                    border: filled ? `1px solid ${colors.primary.main}` : '1px solid rgba(255,255,255,0.08)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {filled && <Coffee sx={{ fontSize: 12, color: '#FFFFFF' }} />}
                  </Box>
                );
              })}
            </Box>
          </GlassCard>
        ))
      )}
    </Container>
  );
}
